import React, { useMemo } from 'react'
import type { ValueInputProps } from '@pushly/aqe'
import { CountInWindowInput, type CountInWindowConfig, type CountInWindowWindowOption } from './count-in-window-input'
import { useSegmentValueInputContext } from './segment-value-input-context'
import { dayWindows } from './windows'

/**
 * Everything a frequency surface states about itself beyond the shared shape.
 *
 * The count and the window are the same on all six; a surface differs only in how
 * far back it can look, how large its count may be, and what the count is a count
 * of. Money is the one thing configuration cannot state ahead of time, because the
 * symbol belongs to the domain rather than to the field.
 */
interface FrequencySurface {
    windowOptions: CountInWindowWindowOption[]
    maxValue: number
    valueLabel: string | null
    tooltip?: string
    /** True where the count is an amount of money rather than a number of events. */
    money?: boolean
}

/** Every surface but notification clicks looks back at most ninety days. */
const SHORT_WINDOWS = dayWindows(1, 7, 14, 30, 60, 90)

/** The count of events these surfaces will report, whatever a reader asks for. */
const COUNT_CEILING = 999

/** A count of events, over the windows the e-commerce surfaces share. */
const EVENT_COUNT: FrequencySurface = {
    windowOptions: SHORT_WINDOWS,
    maxValue: COUNT_CEILING,
    valueLabel: 'times',
}

/**
 * The surfaces the catalog routes here, keyed by the property each one counts.
 *
 * One `inputKey` serves all six, so the field is what tells them apart. A property
 * the catalog adds to this key without an entry here falls back to a plain count
 * over the shared windows rather than failing to render.
 */
const SURFACES: Record<string, FrequencySurface> = {
    'behaviors.notification_clicks': {
        ...EVENT_COUNT,
        // The one surface that can look back a year.
        windowOptions: dayWindows(1, 7, 14, 30, 60, 90, 365),
        tooltip: 'Time frame does not include clickers from the current day.',
    },
    'behaviors.purchase_count': EVENT_COUNT,
    'behaviors.purchase_amount': {
        windowOptions: SHORT_WINDOWS,
        maxValue: 1_000_000,
        // An amount of money is already stated by the currency symbol in the box,
        // so a unit after it would only repeat that.
        valueLabel: null,
        money: true,
    },
    'behaviors.save_item_count': EVENT_COUNT,
    'behaviors.complete_item_count': EVENT_COUNT,
    'behaviors.rate_item_count': EVENT_COUNT,
}

/**
 * One frequency surface's full configuration.
 *
 * A function rather than a table because the currency a money surface counts in belongs to the
 * domain and is not known until the builder is standing. Named and exported so the summary a
 * collapsed row reads a stored count back as is derived from the same answer the control was built
 * from — the window vocabulary above all, which a second copy would silently outlive.
 */
export function frequencyWindowConfig(fieldKey: string, currencySymbol: string): CountInWindowConfig {
    const surface = SURFACES[fieldKey] ?? EVENT_COUNT

    return {
        windowOptions: surface.windowOptions,
        // One, matching every other count surface. A count of zero is satisfied by every subscriber,
        // so a row opening on it is a condition that reads as a filter and selects the whole audience
        // until the reader notices and raises it.
        defaultCount: 1,
        maxValue: surface.maxValue,
        valueLabel: surface.valueLabel,
        valuePrefix: surface.money ? currencySymbol : undefined,
        // A money surface counts an amount; every other surface counts events.
        countLabel: surface.money ? 'Amount' : 'Count',
        expression: { kind: 'none' },
        tooltip: surface.tooltip,
    }
}

/**
 * A count of events, or of money, over a window.
 *
 * These surfaces show no lead-in phrase and no expression: the row above already
 * names what is being counted, and the condition counts every event of that kind.
 */
export function FrequencyWindowInput(props: ValueInputProps<number | null>) {
    const { currencySymbol } = useSegmentValueInputContext()
    const fieldKey = props.field.key

    const config = useMemo(() => frequencyWindowConfig(fieldKey, currencySymbol), [fieldKey, currencySymbol])

    return <CountInWindowInput {...props} config={config} />
}

FrequencyWindowInput.displayName = 'FrequencyWindowInput'
